import noEvent from '../../assets/no_events.png';
import { getCookies } from '../../hooks/useCookies';
// import actimg from '../../assets/act_img.png';
import { REACT_APP_API_FETCH_SHOWS } from '../../redux/apiConstants';
import { DateFormatter, formatTime } from '../../utilityFunctions/utilsFunctions';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

const defaultEventImg =
  'https://marqproduction.s3.amazonaws.com/undefined/photo-1492684223066-81342ee5ff30.jpeg-1772890279082';
const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

// Priority order: in progress → created → ended → everything else
const STATUS_PRIORITY = { 'in progress': 0, created: 1, ended: 2 };

const getSortPriority = (item) => STATUS_PRIORITY[item?.status] ?? 99;

const dedupeAndSort = (items) => {
  const seen = new Set();
  const unique = items.filter((item) => {
    const id = item.id || item._id;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return unique.sort((a, b) => {
    const diff = getSortPriority(a) - getSortPriority(b);
    if (diff !== 0) return diff;
    // Within the same status, sort by startDate descending (most recent first)
    const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
    const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
    return bDate - aDate;
  });
};

const STATUS_TABS = [
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Live', value: 'live' },
  { label: 'Ended', value: 'ended' },
];

const EVENT_TYPE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Seat Booking', value: 'booking' },
  { label: 'Non-Seat Booking', value: 'non-seat-booking' },
  { label: 'RSVP', value: 'rsvp' },
  { label: 'Registration', value: 'form' },
  { label: 'Token', value: 'token' },
];

/** Tab value to API status mapping */
const TAB_TO_STATUS = {
  upcoming: 'created',
  live: 'in progress',
  ended: 'ended',
};

const getLocationDisplay = (item) => {
  const isOnline =
    item.onlineDetails && (item.onlineDetails.platform || item.onlineDetails.streamingUrl);
  if (isOnline) {
    return {
      online: true,
      text: item.onlineDetails.platform
        ? `Online \u2022 ${item.onlineDetails.platform
            .charAt(0)
            .toUpperCase()}${item.onlineDetails.platform.slice(1)}`
        : 'Online \u2022 Streaming',
    };
  }
  if (item.location) {
    const parts = [item.location.venue, item.location.city, item.location.country].filter(Boolean);
    return { online: false, text: parts.length > 0 ? parts.join(', ') : '\u2014' };
  }
  return { online: false, text: '\u2014' };
};

const DEFAULT_STATUS_TAB = 'upcoming';

function ShowCard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const initialStatusTab = STATUS_TABS.some((t) => t.value === statusFromUrl)
    ? statusFromUrl
    : DEFAULT_STATUS_TAB;

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [event, setEvent] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState('');
  const [activeStatusTab, setActiveStatusTab] = useState(initialStatusTab);
  const searchRef = useRef('');
  const activeTypeRef = useRef('');
  const currentPageRef = useRef(1);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const token = `Bearer ${getCookies('Token')}`;

  const filteredEvents = useMemo(() => {
    const statusToMatch = TAB_TO_STATUS[activeStatusTab];
    return statusToMatch ? event.filter((item) => item?.status === statusToMatch) : event;
  }, [event, activeStatusTab]);

  const fetchShows = async (
    pageNum,
    append = false,
    search = '',
    typeFilter = '',
    statusFilter = ''
  ) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const searchParam = search.trim() ? `&searchBy=${encodeURIComponent(search.trim())}` : '';
      const typeParam = typeFilter ? `&eventCategory=${encodeURIComponent(typeFilter)}` : '';
      const statusParam = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : '';
      const resp = await axios.get(
        `${REACT_APP_API_FETCH_SHOWS}page=${pageNum}&limit=${PAGE_LIMIT}${searchParam}${typeParam}${statusParam}`,
        { headers: { Authorization: token } }
      );
      if (resp?.status === 200) {
        const result = resp?.data?.data?.result;
        const newResults = result?.results ?? [];
        const returnedPage = result?.page ?? pageNum;
        setEvent((prev) => {
          const merged = append ? [...prev, ...newResults] : newResults;
          return dedupeAndSort(merged);
        });
        setCurrentPage(returnedPage);
        currentPageRef.current = returnedPage;
        setHasMore(returnedPage < (result?.totalPages ?? 0));
      }
    } catch (err) {
      // silently handle fetch error
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const activeStatusRef = useRef(initialStatusTab);

  const handleLoadMore = () => {
    const statusFilter = TAB_TO_STATUS[activeStatusRef.current] || '';
    fetchShows(
      currentPageRef.current + 1,
      true,
      searchRef.current,
      activeTypeRef.current,
      statusFilter
    );
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    searchRef.current = val;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      currentPageRef.current = 1;
      const statusFilter = TAB_TO_STATUS[activeStatusRef.current] || '';
      fetchShows(1, false, val, activeTypeRef.current, statusFilter);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleTypeFilter = (typeValue) => {
    setActiveType(typeValue);
    activeTypeRef.current = typeValue;
    currentPageRef.current = 1;
    const statusFilter = TAB_TO_STATUS[activeStatusRef.current] || '';
    fetchShows(1, false, searchRef.current, typeValue, statusFilter);
  };

  const handleStatusTabChange = (value) => {
    setActiveStatusTab(value);
    activeStatusRef.current = value;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('status', value);
      return next;
    });
    currentPageRef.current = 1;
    const statusFilter = TAB_TO_STATUS[value] || '';
    fetchShows(1, false, searchRef.current, activeTypeRef.current, statusFilter);
  };

  useEffect(() => {
    if (statusFromUrl && STATUS_TABS.some((t) => t.value === statusFromUrl)) {
      setActiveStatusTab(statusFromUrl);
      activeStatusRef.current = statusFromUrl;
    }
  }, [statusFromUrl]);

  useEffect(() => {
    const statusFilter = TAB_TO_STATUS[initialStatusTab] || '';
    fetchShows(1, false, '', '', statusFilter);
    return () => clearTimeout(debounceRef.current);
  }, []);
  return (
    <div>
      <div className="active_show_outer">
        <div className="col-md-12">
          {/* Status tabs: Upcoming, Live, Ended */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            {STATUS_TABS.map(({ label, value }) => {
              const isActive = activeStatusTab === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handleStatusTabChange(value)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: `1.5px solid ${isActive ? '#132941' : '#dde3ea'}`,
                    background: isActive ? '#132941' : '#f8fafc',
                    color: isActive ? '#fff' : '#5a6a7a',
                    fontSize: '14px',
                    fontWeight: isActive ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
            }}
          >
            <h2 style={{ margin: 0 }}>All Shows</h2>
            <div style={{ position: 'relative', minWidth: '260px', maxWidth: '340px', flex: '1' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8a99aa"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name"
                aria-label="Search shows"
                style={{
                  width: '100%',
                  padding: '9px 12px 9px 36px',
                  borderRadius: '8px',
                  border: '1.5px solid #dde3ea',
                  fontSize: '14px',
                  color: '#132941',
                  outline: 'none',
                  background: '#f8fafc',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#132941';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dde3ea';
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchTerm('');
                    searchRef.current = '';
                    const statusFilter = TAB_TO_STATUS[activeStatusRef.current] || '';
                    fetchShows(1, false, '', activeTypeRef.current, statusFilter);
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#8a99aa',
                    fontSize: '16px',
                    lineHeight: 1,
                  }}
                >
                  &#x2715;
                </button>
              )}
            </div>
          </div>
          {/* ── Event type filter pills ── */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '20px',
            }}
          >
            {EVENT_TYPE_FILTERS.map(({ label, value }) => {
              const isActive = activeType === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handleTypeFilter(value)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '20px',
                    border: `1.5px solid ${isActive ? '#132941' : '#dde3ea'}`,
                    background: isActive ? '#132941' : '#f8fafc',
                    color: isActive ? '#fff' : '#5a6a7a',
                    fontSize: '13px',
                    fontWeight: isActive ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {!loading && (
            <div className="show_box_main">
              {event && event.length > 0 && filteredEvents.length > 0 ? (
                <>
                  <div className="showbox_sec">
                    {filteredEvents.map((item) => (
                      <div
                        key={item.id || item._id}
                        className="show_box"
                        onClick={() => {
                          const showId = item.id || item._id;
                          if (!showId) return;
                          if (item.eventType === 'token') {
                            navigate(`/shows/view-event/${showId}`);
                            return;
                          }
                          navigate(`/shows/view-users/${showId}`);
                        }}
                      >
                        <div className="active_show_inner">
                          <div className="act_box_img">
                            <Image src={item.image ? item.image : defaultEventImg} alt="" />
                          </div>
                          <div className="act_box_cnt">
                            <div className="act_box_bdg">
                              <span
                                className={`${
                                  item.bookingPaused
                                    ? 'paused'
                                    : item.bookingStatus === 'sold-out' ||
                                      item.status === 'ended' ||
                                      item.status === 'cancelled'
                                    ? 'sold'
                                    : ''
                                }`}
                              >
                                {item.bookingPaused
                                  ? 'Paused'
                                  : item.bookingStatus === 'sold-out'
                                  ? 'Sold-out'
                                  : item.status === 'created'
                                  ? 'Upcoming'
                                  : item.status === 'in progress'
                                  ? 'Ongoing'
                                  : item.status === 'cancelled'
                                  ? 'Cancelled'
                                  : 'Ended'}
                              </span>
                            </div>
                            <h3>{item.eventName}</h3>
                            {(() => {
                              const loc = getLocationDisplay(item);
                              return (
                                <p>
                                  {loc.online ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#132941"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <circle cx="12" cy="12" r="10" />
                                      <line x1="2" y1="12" x2="22" y2="12" />
                                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="19"
                                      viewBox="0 0 18 19"
                                      fill="none"
                                    >
                                      <path
                                        d="M9 16.9762C13.1421 16.9762 16.5 15.4652 16.5 13.6012C16.5 12.6513 15.6278 11.7929 14.2241 11.1796C13.3675 12.7515 12.0613 14.1064 10.4021 14.8154C9.51032 15.1965 8.48968 15.1965 7.59786 14.8154C5.93871 14.1064 4.6325 12.7515 3.77594 11.1796C2.37221 11.7929 1.5 12.6513 1.5 13.6012C1.5 15.4652 4.85786 16.9762 9 16.9762Z"
                                        fill="#132941"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.75 6.86199C3.75 4.16354 6.10051 1.97601 9 1.97601C11.8995 1.97601 14.25 4.16354 14.25 6.86199C14.25 9.53929 12.5744 12.6635 9.96005 13.7807C9.35061 14.0411 8.64939 14.0411 8.03995 13.7807C5.42562 12.6635 3.75 9.53929 3.75 6.86199ZM9 8.72601C9.82843 8.72601 10.5 8.05444 10.5 7.22601C10.5 6.39759 9.82843 5.72601 9 5.72601C8.17157 5.72601 7.5 6.39759 7.5 7.22601C7.5 8.05444 8.17157 8.72601 9 8.72601Z"
                                        fill="#132941"
                                      />
                                    </svg>
                                  )}
                                  <span>{loc.text}</span>
                                </p>
                              );
                            })()}
                            <p>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                fill="none"
                              >
                                <path
                                  d="M5.8125 2.35101C5.8125 2.04035 5.56066 1.78851 5.25 1.78851C4.93934 1.78851 4.6875 2.04035 4.6875 2.35101V3.53546C3.608 3.6219 2.89933 3.83404 2.37868 4.35469C1.85803 4.87534 1.64588 5.58402 1.55944 6.66351H16.4406C16.3541 5.58402 16.142 4.87534 15.6213 4.35469C15.1007 3.83404 14.392 3.6219 13.3125 3.53546V2.35101C13.3125 2.04035 13.0607 1.78851 12.75 1.78851C12.4393 1.78851 12.1875 2.04035 12.1875 2.35101V3.48569C11.6886 3.47601 11.1293 3.47601 10.5 3.47601H7.5C6.87072 3.47601 6.31145 3.47601 5.8125 3.48569V2.35101Z"
                                  fill="#132941"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M1.5 9.47601C1.5 8.84674 1.5 8.28746 1.50968 7.78851H16.4903C16.5 8.28746 16.5 8.84674 16.5 9.47601V10.976C16.5 13.8044 16.5 15.2187 15.6213 16.0973C14.7426 16.976 13.3284 16.976 10.5 16.976H7.5C4.67157 16.976 3.25736 16.976 2.37868 16.0973C1.5 15.2187 1.5 13.8044 1.5 10.976V9.47601ZM12.75 10.976C13.1642 10.976 13.5 10.6402 13.5 10.226C13.5 9.8118 13.1642 9.47601 12.75 9.47601C12.3358 9.47601 12 9.8118 12 10.226C12 10.6402 12.3358 10.976 12.75 10.976ZM12.75 13.976C13.1642 13.976 13.5 13.6402 13.5 13.226C13.5 12.8118 13.1642 12.476 12.75 12.476C12.3358 12.476 12 12.8118 12 13.226C12 13.6402 12.3358 13.976 12.75 13.976ZM9.75 10.226C9.75 10.6402 9.41421 10.976 9 10.976C8.58579 10.976 8.25 10.6402 8.25 10.226C8.25 9.8118 8.58579 9.47601 9 9.47601C9.41421 9.47601 9.75 9.8118 9.75 10.226ZM9.75 13.226C9.75 13.6402 9.41421 13.976 9 13.976C8.58579 13.976 8.25 13.6402 8.25 13.226C8.25 12.8118 8.58579 12.476 9 12.476C9.41421 12.476 9.75 12.8118 9.75 13.226ZM5.25 10.976C5.66421 10.976 6 10.6402 6 10.226C6 9.8118 5.66421 9.47601 5.25 9.47601C4.83579 9.47601 4.5 9.8118 4.5 10.226C4.5 10.6402 4.83579 10.976 5.25 10.976ZM5.25 13.976C5.66421 13.976 6 13.6402 6 13.226C6 12.8118 5.66421 12.476 5.25 12.476C4.83579 12.476 4.5 12.8118 4.5 13.226C4.5 13.6402 4.83579 13.976 5.25 13.976Z"
                                  fill="#132941"
                                />
                              </svg>
                              <span>
                                {item?.startDate &&
                                !Number.isNaN(new Date(item.startDate).getTime())
                                  ? DateFormatter(item.startDate, 'toEventViewDate')
                                  : '—'}{' '}
                                | {item?.startTime != null ? formatTime(item.startTime) : '—'}{' '}
                                {item?.timezoneCode ?? ''}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '24px',
                      marginBottom: '8px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!loadingMore && hasMore) handleLoadMore();
                      }}
                      disabled={loadingMore || !hasMore}
                      aria-label={hasMore ? 'Load more shows' : 'No more shows to load'}
                      style={{
                        padding: '10px 32px',
                        borderRadius: '8px',
                        border: '1.5px solid #132941',
                        background: loadingMore || !hasMore ? '#e0e6ed' : '#132941',
                        color: loadingMore || !hasMore ? '#132941' : '#fff',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: loadingMore || !hasMore ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: '140px',
                      }}
                    >
                      {loadingMore ? 'Loading...' : hasMore ? 'Load More' : 'No More Events'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="no_show_box">
                  <Image src={noEvent} alt="no events" />
                  <h6>
                    {filteredEvents.length === 0 && event.length > 0
                      ? 'No events found.'
                      : searchTerm.trim() && activeType
                      ? `No "${
                          EVENT_TYPE_FILTERS.find((f) => f.value === activeType)?.label
                        }" shows found for "${searchTerm.trim()}"`
                      : searchTerm.trim()
                      ? `No shows found for "${searchTerm.trim()}"`
                      : activeType
                      ? `No "${
                          EVENT_TYPE_FILTERS.find((f) => f.value === activeType)?.label
                        }" events found.`
                      : 'There is no active event.'}
                  </h6>
                </div>
              )}

              {/* <div className="show_box">
            <div className="active_show_inner">
            <div className="act_box_img">
            <Image src={actimg0} alt="" />
            </div>
            <div className="act_box_cnt">
            <div className="act_box_bdg">
            <span className="sold">Sold Out</span>
            </div>
            <h3>Shaan Rahman USA & Canada Tour</h3>
            <p>
            <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            >
            <path
            d="M9 16.9762C13.1421 16.9762 16.5 15.4652 16.5 13.6012C16.5 12.6513 15.6278 11.7929 14.2241 11.1796C13.3675 12.7515 12.0613 14.1064 10.4021 14.8154C9.51032 15.1965 8.48968 15.1965 7.59786 14.8154C5.93871 14.1064 4.6325 12.7515 3.77594 11.1796C2.37221 11.7929 1.5 12.6513 1.5 13.6012C1.5 15.4652 4.85786 16.9762 9 16.9762Z"
            fill="#132941"
            />
            <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.75 6.86199C3.75 4.16354 6.10051 1.97601 9 1.97601C11.8995 1.97601 14.25 4.16354 14.25 6.86199C14.25 9.53929 12.5744 12.6635 9.96005 13.7807C9.35061 14.0411 8.64939 14.0411 8.03995 13.7807C5.42562 12.6635 3.75 9.53929 3.75 6.86199ZM9 8.72601C9.82843 8.72601 10.5 8.05444 10.5 7.22601C10.5 6.39759 9.82843 5.72601 9 5.72601C8.17157 5.72601 7.5 6.39759 7.5 7.22601C7.5 8.05444 8.17157 8.72601 9 8.72601Z"
            fill="#132941"
            />
            </svg>
            <span>The Beacon Theatre, New York</span>
            </p>
            <p>
            <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            >
            <path
            d="M5.8125 2.35101C5.8125 2.04035 5.56066 1.78851 5.25 1.78851C4.93934 1.78851 4.6875 2.04035 4.6875 2.35101V3.53546C3.608 3.6219 2.89933 3.83404 2.37868 4.35469C1.85803 4.87534 1.64588 5.58402 1.55944 6.66351H16.4406C16.3541 5.58402 16.142 4.87534 15.6213 4.35469C15.1007 3.83404 14.392 3.6219 13.3125 3.53546V2.35101C13.3125 2.04035 13.0607 1.78851 12.75 1.78851C12.4393 1.78851 12.1875 2.04035 12.1875 2.35101V3.48569C11.6886 3.47601 11.1293 3.47601 10.5 3.47601H7.5C6.87072 3.47601 6.31145 3.47601 5.8125 3.48569V2.35101Z"
            fill="#132941"
            />
            <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.5 9.47601C1.5 8.84674 1.5 8.28746 1.50968 7.78851H16.4903C16.5 8.28746 16.5 8.84674 16.5 9.47601V10.976C16.5 13.8044 16.5 15.2187 15.6213 16.0973C14.7426 16.976 13.3284 16.976 10.5 16.976H7.5C4.67157 16.976 3.25736 16.976 2.37868 16.0973C1.5 15.2187 1.5 13.8044 1.5 10.976V9.47601ZM12.75 10.976C13.1642 10.976 13.5 10.6402 13.5 10.226C13.5 9.8118 13.1642 9.47601 12.75 9.47601C12.3358 9.47601 12 9.8118 12 10.226C12 10.6402 12.3358 10.976 12.75 10.976ZM12.75 13.976C13.1642 13.976 13.5 13.6402 13.5 13.226C13.5 12.8118 13.1642 12.476 12.75 12.476C12.3358 12.476 12 12.8118 12 13.226C12 13.6402 12.3358 13.976 12.75 13.976ZM9.75 10.226C9.75 10.6402 9.41421 10.976 9 10.976C8.58579 10.976 8.25 10.6402 8.25 10.226C8.25 9.8118 8.58579 9.47601 9 9.47601C9.41421 9.47601 9.75 9.8118 9.75 10.226ZM9.75 13.226C9.75 13.6402 9.41421 13.976 9 13.976C8.58579 13.976 8.25 13.6402 8.25 13.226C8.25 12.8118 8.58579 12.476 9 12.476C9.41421 12.476 9.75 12.8118 9.75 13.226ZM5.25 10.976C5.66421 10.976 6 10.6402 6 10.226C6 9.8118 5.66421 9.47601 5.25 9.47601C4.83579 9.47601 4.5 9.8118 4.5 10.226C4.5 10.6402 4.83579 10.976 5.25 10.976ZM5.25 13.976C5.66421 13.976 6 13.6402 6 13.226C6 12.8118 5.66421 12.476 5.25 12.476C4.83579 12.476 4.5 12.8118 4.5 13.226C4.5 13.6402 4.83579 13.976 5.25 13.976Z"
            fill="#132941"
            />
            </svg>
            <span>March 15, 2025 | 7:30 PM</span>
            </p>
              </div>
              </div>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShowCard;
